export const findDatastreamById = (list, rawId) => {
	const safeList = Array.isArray(list) ? list : [];
	if (rawId === null || rawId === undefined || rawId === '') return null;
	const numericId = Number(rawId);
	if (Number.isFinite(numericId)) {
		const byNumericId = safeList.find(ds => Number(ds?.id) === numericId);
		if (byNumericId) return byNumericId;
	}
	return safeList.find(ds => String(ds?.id) === String(rawId)) || null;
};

export const normalizeDatastreamPin = (rawPin) => {
	const normalizedPin = String(rawPin ?? '').trim();
	if (!normalizedPin || normalizedPin.toLowerCase() === 'pin') {
		return null;
	}
	return normalizedPin;
};

export const findDatastreamByPin = (list, rawPin) => {
	const safeList = Array.isArray(list) ? list : [];
	const normalizedPin = normalizeDatastreamPin(rawPin);
	if (!normalizedPin) return null;
	return safeList.find(ds => normalizeDatastreamPin(ds?.pin) === normalizedPin) || null;
};

export const findDatastreamByReference = (list, rawId, rawPin) => (
	findDatastreamById(list, rawId) || findDatastreamByPin(list, rawPin)
);

export const getDatastreamMinMax = (ds) => {
	if (!ds) return { min: null, max: null };
	const rawMin = ds.min ?? ds.minimum ?? ds.minValue ?? ds.min_value;
	const rawMax = ds.max ?? ds.maximum ?? ds.maxValue ?? ds.max_value;
	const min = rawMin === '' || rawMin === null || rawMin === undefined ? null : Number(rawMin);
	const max = rawMax === '' || rawMax === null || rawMax === undefined ? null : Number(rawMax);
	return {
		min: Number.isFinite(min) ? min : null,
		max: Number.isFinite(max) ? max : null,
	};
};

export const getEffectiveMinMax = (ds, fallbackProps) => {
	const { min: dsMin, max: dsMax } = getDatastreamMinMax(ds);
	const propMin = fallbackProps && Number.isFinite(Number(fallbackProps.min)) ? Number(fallbackProps.min) : null;
	const propMax = fallbackProps && Number.isFinite(Number(fallbackProps.max)) ? Number(fallbackProps.max) : null;
	let min = dsMin ?? propMin;
	let max = dsMax ?? propMax;
	if (Number.isFinite(min) && Number.isFinite(max) && max < min) {
		const lower = Math.min(min, max);
		const upper = Math.max(min, max);
		min = lower;
		max = upper;
	}
	return { min, max };
};

export const clampToMinMax = (val, ds, fallbackProps) => {
	if (val === '' || val === null || val === undefined) return val;
	const { min, max } = getEffectiveMinMax(ds, fallbackProps);
	const num = typeof val === 'number' ? val : Number(val);
	if (!Number.isFinite(num)) {
		const raw = typeof val === 'string' ? val.trim() : '';
		const looksNumeric = raw !== '' && /^[-+]?(\d+(\.\d*)?|\.\d+)(e[-+]?\d+)?$/i.test(raw);
		if (looksNumeric) {
			if (raw.startsWith('-') && min !== null) return min;
			if (!raw.startsWith('-') && max !== null) return max;
			if (min !== null && max !== null && min === max) return min;
		}
		return val;
	}
	if (min !== null && num < min) return min;
	if (max !== null && num > max) return max;
	return num;
};

export const getNumericStepPrecision = (value) => {
	const raw = String(value ?? '').trim().toLowerCase();
	if (!raw) return 0;
	if (raw.includes('e-')) {
		const scientificPrecision = Number(raw.split('e-')[1]);
		return Number.isFinite(scientificPrecision) ? scientificPrecision : 0;
	}
	const fractional = raw.split('.')[1] || '';
	return fractional.length;
};

export const snapValueToStep = (value, stepValue, minValue = 0) => {
	const numericValue = Number(value);
	const numericStep = Number(stepValue);
	const numericMin = Number(minValue);
	if (!Number.isFinite(numericValue) || !Number.isFinite(numericStep) || numericStep <= 0) {
		return value;
	}
	const baseValue = Number.isFinite(numericMin) ? numericMin : 0;
	const precision = Math.max(
		getNumericStepPrecision(numericStep),
		getNumericStepPrecision(baseValue),
	);
	const snappedValue = Math.round((numericValue - baseValue) / numericStep) * numericStep + baseValue;
	return Number(snappedValue.toFixed(precision));
};

export const isStringDatastreamValue = (datastream) => {
	const dt = datastream?.dataType ?? datastream?.type ?? datastream?.datatype;
	return String(dt || '').toLowerCase() === 'string';
};

export const isNumericDatastreamValue = (datastream) => {
	const dt = String(datastream?.dataType ?? datastream?.type ?? datastream?.datatype ?? '').toLowerCase();
	return ['integer', 'int', 'double', 'float', 'number'].includes(dt);
};

export const normalizeSliderValueInputChange = (nextValue, previousValue, options = {}) => {
	const {
		stepValue,
		ds,
		fallbackProps,
		nativeInputType,
		nativeInputData,
	} = options;

	const clampedValue = clampToMinMax(nextValue, ds, fallbackProps);
	const numericStep = Number(stepValue);
	const numericNextValue = Number(clampedValue);
	const numericPreviousValue = Number(previousValue);

	if (
		!Number.isFinite(numericStep) ||
		numericStep <= 0 ||
		!Number.isFinite(numericNextValue) ||
		!Number.isFinite(numericPreviousValue)
	) {
		return clampedValue;
	}

	const delta = numericNextValue - numericPreviousValue;
	if (delta === 0 || Math.abs(delta) >= numericStep) {
		return clampedValue;
	}

	const normalizedInputType = typeof nativeInputType === 'string' ? nativeInputType : '';
	const isDirectTextEntry = normalizedInputType === 'insertText'
		|| normalizedInputType === 'insertFromPaste'
		|| normalizedInputType === 'deleteContentBackward'
		|| normalizedInputType === 'deleteContentForward';
	const likelyNativeStepper = nativeInputData == null && !isDirectTextEntry;

	if (!likelyNativeStepper) {
		return clampedValue;
	}

	const steppedValue = numericPreviousValue + (Math.sign(delta) * numericStep);
	return clampToMinMax(steppedValue, ds, fallbackProps);
};

export const remapColorStopsForDatastream = (stops, previousDs, nextDs) => {
	if (!Array.isArray(stops) || stops.length === 0) return [];

	const normalizedStops = stops
		.filter((s) => s && Number.isFinite(Number(s.value)))
		.map((s) => ({ ...s, value: Number(s.value) }))
		.sort((a, b) => a.value - b.value);

	if (normalizedStops.length === 0) return [];

	const { min: nextMin, max: nextMax } = getDatastreamMinMax(nextDs);
	if (!Number.isFinite(nextMin) || !Number.isFinite(nextMax) || nextMax <= nextMin) {
		return normalizedStops;
	}

	const { min: prevDsMin, max: prevDsMax } = getDatastreamMinMax(previousDs);
	let prevMin = Number.isFinite(prevDsMin) ? prevDsMin : null;
	let prevMax = Number.isFinite(prevDsMax) ? prevDsMax : null;

	if (!(prevMax > prevMin)) {
		const allPercent = normalizedStops.every((s) => s.value >= 0 && s.value <= 100);
		if (allPercent) {
			prevMin = 0;
			prevMax = 100;
		} else {
			prevMin = normalizedStops[0].value;
			prevMax = normalizedStops[normalizedStops.length - 1].value;
		}
	}

	const prevRange = prevMax - prevMin;
	if (!(prevRange > 0)) return normalizedStops;

	const nextRange = nextMax - nextMin;
	return normalizedStops
		.map((s) => {
			const ratio = Math.max(0, Math.min(1, (s.value - prevMin) / prevRange));
			const remappedValue = nextMin + (ratio * nextRange);
			return { ...s, value: Number(remappedValue.toFixed(2)) };
		})
		.sort((a, b) => a.value - b.value);
};
